import { saveAs } from '@progress/kendo-file-saver';

// Basic general file functions:

export const validFileTypes = ['.doc', '.docx', '.ppt', '.pptx', '.xls', '.xlsx', '.pdf', '.xlsm', '.jpeg', '.jpg', '.png', '.gif', '.tif', '.tiff', '.webp', '.svg', '.msg'];

export const validFileTypesForLibro = ['.doc', '.docx', '.ppt', '.pptx', '.xls', '.xlsx', '.pdf', '.jpeg', '.jpg', '.png', '.gif', '.tif', '.tiff', '.svg', '.csv', '.eml', '.msg'];

export const maxFileSizeForLibroMB = 25;

export const maxFileSizeForLibro = maxFileSizeForLibroMB * 1024 * 1024; // 26214400

export const maxFileSizeMB = 25;

export const isValidFileType = (fileObj) => { return validFileTypes.some(type => fileObj.name.toLowerCase().includes(type)); }

export const isImageFile = (fileObj) => {
    const imageExtensions = ['.jpeg', '.jpg', '.gif', '.tif', '.tiff', '.webp', '.svg'];
    return imageExtensions.includes(fileObj.name.toLowerCase().slice(-5));
}

export const saveFileString = (fileName, fileString, extension) => {
    const byteCharacters = atob(fileString);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: extension });
        saveAs(blob, fileName);
}

export const shortenFileName = (fileName) => {
    if (fileName.length > 20) {
        let fileNameArr = fileName.split('.')
        let fileExtension = fileNameArr[fileNameArr.length - 1];

        return (fileName.slice(0, 20 - fileExtension.length - 4) + "...." + fileExtension);
    } else {
        return fileName;
    }
}

export const getFileTypeExtension = (filename) => {
    return filename?.substring(filename?.lastIndexOf(".") + 1, filename?.length) || filename;
};

export const mentionDisplayTransform = (id, display, type) => `@${display}:`;

export const setNewCommentState = (newCommentText) => newCommentText.replaceAll(/\[/g, "").replaceAll(/\]/g, ":");

export const checkInvalidFile = (fileObj) => {
    let alertInfo = {
        validFile: true,
        alertMessage: "",
        sizeExceeded: "",
        showAlert: false,
        alertVariant: "",
        alertAction: ""
    };

    if (fileObj) {
        if (!isValidFileType(fileObj)) {
            alertInfo.alertMessage = "File type is not supported";
            alertInfo.validFile = false;
        } else if (fileObj.size > 52428800) { // 50MB
            alertInfo.alertMessage = "File size exceeds 50MB";
            alertInfo.sizeExceeded = "50MB";
            alertInfo.validFile = false;
        } else if (fileObj.size > 10485760 && isImageFile(fileObj)) { // 10MB
            alertInfo.alertMessage = "File size exceeds 10MB";
            alertInfo.sizeExceeded = "10MB";
            alertInfo.validFile = false;
        }
    } else {
        alertInfo.alertMessage = "File cannot be uploaded";
        alertInfo.validFile = false;
    }

    alertInfo.showAlert = !alertInfo.validFile;
    alertInfo.alertVariant = "warning";
    alertInfo.alertAction = "Cannot Attach: ";

    return alertInfo;
};

export const checkInvalidFiles = (files, validFileTypeArr = validFileTypes) => {
    let fileResult = { error: false, message: "", data: {} };

    let isFileTypeInvalid = files.filter(
      (file) => validFileTypeArr.indexOf(`.${file.name.split(".")[1]}`) === -1
    );
    let doesFileSizeExceed = files.filter((file) => file.size > 52428800);

    if (isFileTypeInvalid.length > 0) {
      fileResult = {
        ...fileResult,
        error: true,
        message: "Some invalid file types.",
      };
    } else if (doesFileSizeExceed.length > 0) {
      fileResult = {
        ...fileResult,
        error: true,
        message: "File must be under 50Mb to upload.",
      };
    }

    return fileResult;
  };

export const getUsersMatchingQuery = (tags, query, callback) => {
    const matchedUsers = tags.map((tagItem, index) => ({
        id: index,
        display: tagItem
    })).filter(user => {
        const names = user.display.split(" ");
        return names.some(name => name.toLowerCase().startsWith(query.toLowerCase()));
    });
    callback(matchedUsers);
};

// File manipulation :

export const processFiles = (newFiles, selectedFiles, setSelectedFiles, handleFileCheck) => {
    const updatedFiles = [...selectedFiles];
    newFiles.forEach(file => {
        if (!updatedFiles.some(f => f.name === file.name)) {
            const isValidFile = handleFileCheck(file);
            if (isValidFile) updatedFiles.push(file);
        }
    });
    setSelectedFiles(updatedFiles);
};

export const browseFile = (e, selectedFiles, setSelectedFiles, handleFileCheck) => {
    e.preventDefault();
    e.stopPropagation();
    let newFiles = Array.from(e.target.files);
    e.target.value = null;
    processFiles(newFiles, selectedFiles, setSelectedFiles, handleFileCheck);
};

export const drop = (e, selectedFiles, setSelectedFiles, handleFileCheck) => {
    e.preventDefault();
    e.stopPropagation();
    let newFiles = Array.from(e.dataTransfer.files);
    processFiles(newFiles, selectedFiles, setSelectedFiles, handleFileCheck);
};

export const deleteBrowseFile = (e, name, selectedFiles, setSelectedFiles) => {
    e.preventDefault();
    e.stopPropagation();
    const updatedFiles = selectedFiles.filter(file => file.name !== name);
    setSelectedFiles(updatedFiles);
};

export const dragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
};

export const dragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
};

export const dragStart = (e) => {
    e.dataTransfer.clear();
    e.preventDefault();
    e.stopPropagation();
};

export const dragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
};

// Advanced file functions:

export const ToBase64 = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
};
